import Access from 'ui/Access';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import { Permissions } from 'utils/permissions';
import React from 'react';
import StatusChip from 'ui/StatusChip';
import TextField from '@material-ui/core/TextField';
import { Typography } from '@material-ui/core';
import classNames from 'classnames';
import formatters from '../../../../formatters';
import { withStyles } from '@material-ui/core/styles';

const userInfoStyles = theme => ({
    container: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    row: {
        width: '100%',
    },
    checkbox: {
        padding: '4px 9px',
    },
    textField: {
        width: '50%',
    },

    spaceLeft: {
        paddingLeft: 5,
    },
    spaceRight: {
        paddingRight: 5,
    },
    dense: {
        marginTop: 16,
    },
    menu: {
        width: 200,
    },
    chip: {
        height: 18,
        minWidth: 18,
        fontWeight: 500,
        fontSize: 11,
    },
    chipLabel: {
        padding: '0 4px',
    },
    disable2fa: {
        textDecoration: 'underline',
        color: theme.palette.text.blue,
        margin: '0 16px 0 0',
        cursor: 'pointer',
        fontSize: '0.9em',
    },
});

let SimpleUserInfo = props => {
    const {
        classes,
        userInfo,
        userInfoErrors,
        onChange,
        mode,
        hideAdmin,
    } = props;
    const disabled = mode === 'details';
    return (
        <div className={classes.container}>
            <div className={classes.row}>
                <TextField
                    error={Boolean(userInfoErrors.first_name)}
                    helperText={userInfoErrors.first_name}
                    required
                    disabled={disabled}
                    label="First Name"
                    value={userInfo.first_name}
                    onChange={event =>
                        onChange('first_name', event.target.value)
                    }
                    className={classNames(
                        classes.textField,
                        classes.spaceRight
                    )}
                    margin="normal"
                    variant="outlined"
                />
                <TextField
                    error={Boolean(userInfoErrors.last_name)}
                    helperText={userInfoErrors.last_name}
                    required
                    disabled={disabled}
                    label="Last Name"
                    value={userInfo.last_name}
                    onChange={event =>
                        onChange('last_name', event.target.value)
                    }
                    className={classNames(classes.textField, classes.spaceLeft)}
                    margin="normal"
                    variant="outlined"
                />
            </div>
            <div className={classes.row}>
                <TextField
                    required
                    disabled={disabled}
                    error={Boolean(userInfoErrors.email)}
                    helperText={userInfoErrors.email}
                    label="Email"
                    className={classNames(
                        classes.textField,
                        classes.spaceRight
                    )}
                    value={userInfo.email}
                    onChange={event => onChange('email', event.target.value)}
                    margin="normal"
                    variant="outlined"
                />
            </div>
            {!hideAdmin && (
                <div className={classes.row}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                disabled={disabled}
                                checked={userInfo.admin}
                                onChange={event =>
                                    onChange('admin', event.target.checked)
                                }
                                value="admin"
                                color="secondary"
                            />
                        }
                        label="Admin user"
                    />
                </div>
            )}
        </div>
    );
};

SimpleUserInfo = withStyles(userInfoStyles)(SimpleUserInfo);

const getUserStats = currentUser => {
    const { groups } = currentUser;
    return {
        savedItems: currentUser.secure_items ? currentUser.secure_items : 0,
        securityScore: formatters.securityScore(
            currentUser.security_score_business
        ),
        groupsCount: groups ? groups.length : 0,
        twoFA: currentUser.multi_factor_authentication,
        lastBackup:
            currentUser.devices && currentUser.devices.length > 0
                ? formatters.date(currentUser.devices[0].last_sync_date)
                : null,
    };
};

let ExtendedUserInfo = props => {
    const {
        classes,
        currentUser,
        userInfo,
        userInfoErrors,
        onChange,
        mode,
        managedByPb,
        canEditAdmin,
        extraAction,
        hideAdmin,
        loggedInUser,
    } = props;
    const readOnly = mode === 'details' || !managedByPb;
    const readOnlyCheckBox = mode === 'details';
    const readOnlyAdminCheckBox = !canEditAdmin || readOnlyCheckBox;
    const userStats = getUserStats(currentUser);

    return (
        <div className={classes.container}>
            <div className={classes.row}>
                <TextField
                    error={Boolean(userInfoErrors.first_name)}
                    helperText={userInfoErrors.first_name}
                    required
                    disabled={readOnly}
                    label="First Name"
                    value={userInfo.first_name}
                    onChange={event =>
                        onChange('first_name', event.target.value)
                    }
                    className={classNames(
                        classes.textField,
                        classes.spaceRight
                    )}
                    margin="normal"
                    variant="outlined"
                />
                <TextField
                    error={Boolean(userInfoErrors.last_name)}
                    helperText={userInfoErrors.last_name}
                    required
                    disabled={readOnly}
                    label="Last Name"
                    value={userInfo.last_name}
                    onChange={event =>
                        onChange('last_name', event.target.value)
                    }
                    className={classNames(classes.textField, classes.spaceLeft)}
                    margin="normal"
                    variant="outlined"
                />
            </div>
            <div className={classes.row}>
                <TextField
                    required
                    disabled
                    error={Boolean(userInfoErrors.email)}
                    helperText={userInfoErrors.email}
                    label="Email"
                    className={classNames(
                        classes.textField,
                        classes.spaceRight
                    )}
                    value={userInfo.email}
                    onChange={event => onChange('email', event.target.value)}
                    margin="normal"
                    variant="outlined"
                />
            </div>
            <div className={classes.row} style={{ width: '50%' }}>
                <List dense={true}>
                    <ListItem disableGutters={true}>
                        <ListItemText primary="Saved items" />
                        <ListItemSecondaryAction>
                            <Chip
                                color="secondary"
                                label={userStats.savedItems}
                                classes={{
                                    root: classes.chip,
                                    label: classes.chipLabel,
                                }}
                            />
                        </ListItemSecondaryAction>
                    </ListItem>
                    <Divider light />
                    <ListItem disableGutters={true}>
                        <ListItemText primary="Security Score" />
                        <ListItemSecondaryAction>
                            <Chip
                                color="secondary"
                                label={userStats.securityScore}
                                classes={{
                                    root: classes.chip,
                                    label: classes.chipLabel,
                                }}
                            />
                        </ListItemSecondaryAction>
                    </ListItem>
                    <Divider light />
                    <ListItem disableGutters={true}>
                        <ListItemText primary="Groups" />
                        <ListItemSecondaryAction>
                            <Chip
                                color="secondary"
                                label={userStats.groupsCount}
                                classes={{
                                    root: classes.chip,
                                    label: classes.chipLabel,
                                }}
                            />
                        </ListItemSecondaryAction>
                    </ListItem>
                    <Divider light />
                    <ListItem disableGutters={true}>
                        <ListItemText primary="2FA" />
                        <ListItemSecondaryAction>
                            {userStats.twoFA ? (
                                <>
                                    <Disable2FA
                                        onChange={extraAction('disable-2fa')}
                                    />
                                    <StatusChip
                                        label="Enabled"
                                        variant="active"
                                        size="small"
                                    />
                                </>
                            ) : (
                                <StatusChip
                                    size="small"
                                    label="Disabled"
                                    variant="disable"
                                />
                            )}
                        </ListItemSecondaryAction>
                    </ListItem>
                    <Divider light />
                    <ListItem disableGutters={true}>
                        <ListItemText primary="Last backup" />
                        <ListItemSecondaryAction>
                            <Typography variant="body2">
                                {userStats.lastBackup}
                            </Typography>
                        </ListItemSecondaryAction>
                    </ListItem>
                    <Divider light />
                </List>
            </div>
            {!hideAdmin && (
                <div className={classes.row}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                classes={{ root: classes.checkbox }}
                                disabled={readOnlyAdminCheckBox}
                                checked={userInfo.admin}
                                onChange={event =>
                                    onChange('admin', event.target.checked)
                                }
                                value="admin"
                                color="secondary"
                            />
                        }
                        label="Admin user"
                    />
                </div>
            )}
            <div className={classes.row}>
                <FormControlLabel
                    control={
                        <Checkbox
                            classes={{ root: classes.checkbox }}
                            disabled={readOnlyCheckBox}
                            checked={userInfo.master_password_change_required}
                            onChange={event =>
                                onChange(
                                    'master_password_change_required',
                                    event.target.checked
                                )
                            }
                            value="master_password_change_required"
                            color="secondary"
                        />
                    }
                    label="Must change password"
                />
            </div>
            {loggedInUser.email !== currentUser.email && (
                <div className={classes.row}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                classes={{ root: classes.checkbox }}
                                disabled={readOnlyCheckBox}
                                checked={userInfo.disabled}
                                onChange={event =>
                                    onChange('disabled', event.target.checked)
                                }
                                value="enabled"
                                color="secondary"
                            />
                        }
                        label="Account disabled"
                    />
                </div>
            )}
        </div>
    );
};
const withUserInfoStyles = withStyles(userInfoStyles);

const Disable2FA = withUserInfoStyles(({ onChange, classes }) => (
    <Access.Change permission={Permissions.COMPANIES_DISABLE_USER_2FA}>
        <span className={classes.disable2fa} onClick={onChange}>
            Disable 2FA
        </span>
    </Access.Change>
));

ExtendedUserInfo = withUserInfoStyles(ExtendedUserInfo);

export { SimpleUserInfo, ExtendedUserInfo };
